/**
 * Accounts CRM — list-style account management surface.
 *
 * Replaces the old kanban that sat under People > Accounts. The
 * Workflows kanban (Projects > Workflows) is the right place to drag
 * stages around; this view is for ACCOUNT-level work — the things a
 * sales/CS person needs to see at a glance per company:
 *
 *   - search by name / POC / owner
 *   - phase filter (Sales / Implementations / Customer Success)
 *     derived from the rolled-up earliest workflow stage
 *   - one row per account with primary POC, owner, current rollup
 *     stage + workflow count, and last-updated
 *   - clicking a row opens the existing AccountDetailModal
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  Building2,
  Clock3,
  Inbox,
  LayoutGrid,
  List,
  Loader2,
  MoveLeft,
  MoveRight,
  PanelLeft,
  Plus,
  Search,
  SlidersHorizontal,
  Upload,
  UserCircle2,
  X,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { LoadingState } from '../components/States';
import { useToast } from '../components/ToastProvider';
import { ViewSwitcher } from '../components/ViewSwitcher';
import { FilterBuilder, type FilterFieldOption } from '../components/FilterBuilder';
import {
  defaultView,
  isFilterNode,
  packViewFilters,
  reorderColumns,
  switchViewType,
  toggleColumn,
  unpackViewFilters,
  visibleColumns,
  type FilterNode,
  type SavedView as RichSavedView,
  type ViewType,
} from '../lib/crm/viewConfig';
import {
  applyView,
  debounceFilter,
  deriveViewStatus,
  isNoMatch,
  resolveActiveView,
} from '../lib/crm/viewRuntime';
import {
  removeSavedView,
  saveView,
  subscribeSavedViews,
} from '../lib/db/savedViews';
import { C } from '../lib/tokens';
import {
  ARGOIQ_CUSTOMERS,
  buildAccountDraft,
  filterNewNames,
  parseAccountNames,
} from '../lib/crm/accountImport';
import {
  bootstrapAccountsIfEmpty,
  createAccount,
  subscribeAccounts,
} from '../lib/db/accounts';
import {
  accountRollupStage,
  STAGE_PHASE,
  type Account,
  type PipelinePhase,
} from '../lib/db/types';
import { AccountDetailModal } from '../components/AccountDetailModal';
import { CrmHygieneCard } from '../components/CrmHygieneCard';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  commitCellEdit,
  nextCell,
  type CellPos,
  type FieldSchema,
} from '../lib/crm/inlineEditController';
import { queueApproval } from '../lib/agent/approvals';
import type { AgentAction } from '../lib/agent/types';

interface PhaseStyle {
  label: string;
  accent: string;
  pillClass: string;
}

const PHASE_STYLES: Record<PipelinePhase, PhaseStyle> = {
  sales: {
    label: 'Sales',
    accent: C.magenta,
    pillClass: 'glass-pill glass-pill--magenta',
  },
  implementations: {
    label: 'Implementations',
    accent: C.blueElectric,
    pillClass: 'glass-pill glass-pill--blue',
  },
  'customer-success': {
    label: 'Customer Success',
    accent: C.green,
    pillClass: 'glass-pill glass-pill--green',
  },
};

// Remember the active saved view across reloads.
const ACCOUNTS_ACTIVE_VIEW_KEY = 'argo.accounts.activeView';

/** The fields a saved view can filter / column-configure on accounts.
 *  `stage` and `workflowCount` are derived per-row (rollup) so views can
 *  lens on them like real fields. */
const ACCOUNT_VIEW_FIELDS = [
  'name',
  'pocName',
  'ownerName',
  'stage',
  'workflowCount',
  'updatedAt',
] as const;

const FIELD_LABELS: Record<string, string> = {
  name: 'Account',
  pocName: 'Primary POC',
  ownerName: 'Owner',
  stage: 'Stage (rollup)',
  workflowCount: 'Workflows',
  updatedAt: 'Last update',
};

const FILTER_FIELD_OPTIONS: FilterFieldOption[] = ACCOUNT_VIEW_FIELDS.map((f) => ({
  field: f,
  label: FIELD_LABELS[f],
}));

/** Per-field table column sizing (matches the legacy fixed table). */
const FIELD_WIDTHS: Record<string, string> = {
  name: 'minmax(220px, 1.5fr)',
  pocName: 'minmax(140px, 1fr)',
  ownerName: 'minmax(100px, 0.8fr)',
  stage: 'minmax(180px, 1.2fr)',
  workflowCount: '90px',
  updatedAt: '110px',
};

/** The identity view every account list starts from. */
function accountsDefaultView(ownerId: string): RichSavedView {
  return {
    ...defaultView('accounts', ownerId, [...ACCOUNT_VIEW_FIELDS]),
    name: 'All accounts',
  };
}

/** Count leaf conditions in a filter tree (for the Filters badge). */
function countLeaves(node: FilterNode): number {
  return node.children.reduce(
    (n, c) => n + (isFilterNode(c) ? countLeaves(c) : 1),
    0,
  );
}

/** Enrich an account with the derived fields views can lens on. */
function viewRecordOf(a: Account): Record<string, any> {
  const stage = accountRollupStage(a);
  return { ...a, stage: stage ?? '', workflowCount: a.workflows.length };
}

// Column order for the phase-grouped board. Accounts whose rollup stage
// resolves to none land in the trailing "Unassigned" column.
const BOARD_PHASES: PipelinePhase[] = [
  'sales',
  'implementations',
  'customer-success',
];

// --- Governed inline editing ---------------------------------------------
// Which table cells edit inline, and the schema each edit validates
// against before it may enter the approval queue. Derived/rollup fields
// (stage, workflowCount, updatedAt) are deliberately absent — they are
// computed, not stored, so an "edit" would be a lie. `name` stays
// read-only here because the name cell is the row's open affordance.
const EDITABLE_SCHEMAS: Record<string, FieldSchema> = {
  pocName: { type: 'string', label: 'Primary POC' },
  ownerName: { type: 'string', label: 'Owner' },
};

/** Per-cell commit lifecycle rendered on the grid.
 *  pending → spinner; queued → clock pill (awaiting approval);
 *  error → red border + tooltip (validation or enqueue failure). */
interface CellState {
  status: 'pending' | 'queued' | 'error';
  error?: string;
}

interface AccountsCrmViewProps {
  /** Account to open on arrival (from the ⌘K command palette). The nonce
   *  lets the same account be re-focused twice in a row. */
  focusAccount?: { id: string; nonce: number } | null;
}

export function AccountsCrmView({ focusAccount = null }: AccountsCrmViewProps) {
  const { user } = useAuth();
  const { push } = useToast();
  const canMutate = user?.role !== 'viewer';
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  // First-snapshot flag so the list shows a skeleton, not a premature
  // "no accounts yet", before the store has answered.
  const [loaded, setLoaded] = useState(false);
  // Live search is debounced through the tested viewRuntime helper so a
  // burst of keystrokes filters ONCE: `searchInput` echoes the field
  // instantly, `search` settles 150ms after the operator stops typing.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useMemo(
    () => debounceFilter((q: string) => setSearch(q), 150),
    [],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  const [phaseFilter, setPhaseFilter] = useState<PipelinePhase | null>(null);
  const [openAccountId, setOpenAccountId] = useState<string | null>(null);
  const lastFocusNonce = useRef(0);

  // --- First-class saved views -------------------------------------------
  // The list and the board are two LENSES over one active SavedView. The
  // pure engine lives in src/lib/crm/viewConfig.ts; persistence rides the
  // existing savedViews store API (rich config packed into the payload).
  const userId = user?.uid ?? 'local';
  const [savedViews, setSavedViews] = useState<RichSavedView[]>([]);
  // The identity ("All accounts") view is session-local — edits to its
  // filter/columns/lens live here until the user saves them as a named view.
  const [draftDefault, setDraftDefault] = useState<RichSavedView>(() =>
    accountsDefaultView(userId),
  );
  const [activeViewId, setActiveViewId] = useState<string>(() => {
    try {
      return localStorage.getItem(ACCOUNTS_ACTIVE_VIEW_KEY) ?? 'accounts:default';
    } catch {
      return 'accounts:default';
    }
  });
  const [viewsOpen, setViewsOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(ACCOUNTS_ACTIVE_VIEW_KEY, activeViewId);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [activeViewId]);

  // Live saved views for accounts; legacy flat-filter records (no packed
  // config) are skipped honestly rather than guessed at. The persisted
  // row id becomes the rich view's id so delete/select stay in sync.
  useEffect(() => {
    return subscribeSavedViews((rows) => {
      const rich: RichSavedView[] = [];
      for (const row of rows) {
        const v = unpackViewFilters(row.filters);
        if (v) rich.push({ ...v, id: row.id });
      }
      setSavedViews(rich);
    }, 'accounts');
  }, []);

  // Resolve the active view with the tested fallback guard: a remembered
  // id that points at a deleted view degrades to the user's default /
  // first shared view / the session draft — the list ALWAYS has a lens.
  const activeView: RichSavedView =
    savedViews.find((v) => v.id === activeViewId) ??
    (activeViewId === draftDefault.id
      ? draftDefault
      : resolveActiveView(savedViews, userId, draftDefault));
  const isDefaultActive = activeView.id === draftDefault.id;

  /** Persist a named view through the existing savedViews API (upsert by name). */
  const persistView = async (v: RichSavedView): Promise<void> => {
    try {
      await saveView({ name: v.name, entity: 'accounts', filters: packViewFilters(v) });
    } catch {
      push({ kind: 'error', text: 'Could not save the view. Try again.' });
    }
  };

  /** Route an edit to the right home: draft state or the store. */
  const updateActiveView = (next: RichSavedView) => {
    if (next.id === draftDefault.id) setDraftDefault(next);
    else void persistView(next);
  };

  const handleCreateView = async (name: string) => {
    const clone: RichSavedView = { ...activeView, name, ownerId: userId, pinned: false };
    try {
      const persisted = await saveView({
        name,
        entity: 'accounts',
        filters: packViewFilters(clone),
      });
      setActiveViewId(persisted.id);
      push({ kind: 'success', text: `Saved view “${name}”.` });
    } catch {
      push({ kind: 'error', text: 'Could not create the view. Try again.' });
    }
  };

  const handleDeleteView = async (v: RichSavedView) => {
    try {
      await removeSavedView(v.id);
      if (activeViewId === v.id) setActiveViewId('accounts:default');
    } catch {
      push({ kind: 'error', text: 'Could not delete the view. Try again.' });
    }
  };

  const handleSwitchType = (v: RichSavedView, t: ViewType) => {
    if (v.viewType === t) return;
    // Kanban lanes on this surface group by pipeline phase (the rollup).
    const next = switchViewType(v, t);
    updateActiveView(t === 'kanban' ? { ...next, groupBy: 'phase' } : next);
  };

  useEffect(() => {
    let active = true;
    bootstrapAccountsIfEmpty().catch(() => undefined);
    const unsub = subscribeAccounts((records) => {
      if (active) {
        setAccounts(records);
        setLoaded(true);
      }
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);

  // Entity quick-jump: open the account chosen in the ⌘K palette. Runs on
  // mount too (the tab only renders this view once Accounts is selected).
  useEffect(() => {
    if (!focusAccount || focusAccount.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focusAccount.nonce;
    setOpenAccountId(focusAccount.id);
  }, [focusAccount]);

  const phaseCounts = useMemo(() => {
    const out: Record<PipelinePhase, number> = {
      sales: 0,
      implementations: 0,
      'customer-success': 0,
    };
    for (const a of accounts) {
      const stage = accountRollupStage(a);
      if (!stage) continue;
      out[STAGE_PHASE[stage]]++;
    }
    return out;
  }, [accounts]);

  // ONE canonical filter path: the saved view's compound tree + sort run
  // through the tested viewRuntime (which delegates to filterEngine /
  // viewFilter — no parallel operator logic here), then the toolbar's
  // search + phase quick-filters narrow the result. Counts stay honest
  // (`total` = whole collection) so the UI can tell "empty collection"
  // from "filters matched nothing". A corrupt view NEVER blanks the
  // list: applyView skips malformed clauses, and any unexpected throw
  // degrades to the unfiltered collection behind an error banner.
  const runtime = useMemo(() => {
    const q = search.trim().toLowerCase();
    try {
      // Enrich each row with the derived fields views lens on (rollup
      // stage, workflow count) and keep a handle back to the account.
      const enriched = accounts.map((a) => ({ ...viewRecordOf(a), __src: a }));
      const res = applyView(enriched, activeView);
      let rows = res.rows.map((r) => r.__src as Account);
      if (phaseFilter) {
        rows = rows.filter((a) => {
          const stage = accountRollupStage(a);
          return !!stage && STAGE_PHASE[stage] === phaseFilter;
        });
      }
      if (q) {
        rows = rows.filter((a) => {
          const hay = (
            a.name +
            ' ' +
            (a.pocName ?? '') +
            ' ' +
            (a.pocEmail ?? '') +
            ' ' +
            (a.ownerName ?? '') +
            ' ' +
            (a.assignedTo ?? '') +
            ' ' +
            a.workflows.map((w) => w.name + ' ' + (w.status ?? '')).join(' ')
          ).toLowerCase();
          return hay.includes(q);
        });
      }
      // No explicit view sort → stable name order, as before.
      if (!activeView.sort) rows = rows.slice().sort((a, b) => a.name.localeCompare(b.name));
      return { rows, matched: rows.length, total: accounts.length, error: null as string | null };
    } catch (e) {
      return {
        rows: accounts,
        matched: accounts.length,
        total: accounts.length,
        error: e instanceof Error ? e.message : 'The view filter failed to run.',
      };
    }
  }, [accounts, search, phaseFilter, activeView]);

  const filtered = runtime.rows;
  const viewStatus = deriveViewStatus(runtime, !loaded, runtime.error);
  const filtersHidEverything = isNoMatch(runtime);

  /** "Clear filters" — reset search + phase, and step off a filtered
   *  view (never destroys a saved view's persisted filter). */
  const clearAllFilters = () => {
    debouncedSearch.cancel();
    setSearchInput('');
    setSearch('');
    setPhaseFilter(null);
    if (countLeaves(activeView.filter) > 0) {
      if (isDefaultActive) {
        setDraftDefault((v) => ({ ...v, filter: { op: 'and', children: [] } }));
      } else {
        setActiveViewId(draftDefault.id);
      }
    }
  };

  // Group the (already search/phase-filtered) accounts into board
  // columns by rollup phase; `none` collects accounts with no workflows.
  const byPhase = useMemo(() => {
    const map: Record<PipelinePhase | 'none', Account[]> = {
      sales: [],
      implementations: [],
      'customer-success': [],
      none: [],
    };
    for (const a of filtered) {
      const stage = accountRollupStage(a);
      const phase = stage ? STAGE_PHASE[stage] : null;
      if (phase) map[phase].push(a);
      else map.none.push(a);
    }
    return map;
  }, [filtered]);

  // --- Governed inline cell editing ---------------------------------------
  // Edits never write the store directly: each commit routes through
  // commitCellEdit (validate → old→new diff → enqueue), where enqueue is
  // the existing approval seal (queueApproval → Approvals inbox →
  // resolveApproval executes via the typed adapters). Per-cell lifecycle
  // (pending/queued/error) renders right on the grid.
  const [editingCell, setEditingCell] = useState<CellPos | null>(null);
  const [cellStates, setCellStates] = useState<Record<string, CellState>>({});

  // Editable columns, in visible order — the keyboard grid nextCell
  // navigates (Tab/Enter/Shift-Tab hop between editable cells only).
  const editableCols = useMemo(
    () =>
      visibleColumns(activeView.columns)
        .map((c) => c.field)
        .filter((f) => EDITABLE_SCHEMAS[f] !== undefined),
    [activeView.columns],
  );

  /** Commit one cell edit through the governed path and advance focus. */
  const handleCellCommit = async (
    account: Account,
    field: string,
    value: string,
    pos: CellPos,
    key: 'Enter' | 'Tab' | 'ShiftTab' | null,
  ) => {
    const raw = (account as unknown as Record<string, unknown>)[field];
    const old = raw === undefined || raw === null ? '' : String(raw);
    const cellKey = `${account.id}:${field}`;
    setCellStates((s) => ({ ...s, [cellKey]: { status: 'pending' } }));

    const res = await commitCellEdit(
      { recordId: account.id, field, oldValue: old, newValue: value },
      {
        schema: EDITABLE_SCHEMAS[field],
        enqueue: async (d) => {
          const action: AgentAction = {
            kind: 'update_record_field',
            recordType: 'account',
            recordId: account.id,
            recordName: account.name,
            attr: field,
            from: old.trim() === '' ? null : old,
            to: value,
          };
          const rec = await queueApproval(action, 'chat', `Inline edit — ${d.summary}`);
          return { id: rec.id };
        },
      },
    );

    if (res.status === 'queued') {
      setCellStates((s) => ({ ...s, [cellKey]: { status: 'queued' } }));
      push({
        kind: 'success',
        text: 'Edit queued for approval.',
        detail: `${FIELD_LABELS[field] ?? field}: ${old || '—'} → ${value || '—'}`,
      });
    } else if (res.status === 'noop') {
      // Nothing changed — clear any stale state, spend no attention.
      setCellStates((s) => {
        const { [cellKey]: _drop, ...rest } = s;
        return rest;
      });
    } else {
      setCellStates((s) => ({
        ...s,
        [cellKey]: { status: 'error', error: res.error ?? 'Edit failed' },
      }));
      if (res.status === 'error') {
        push({ kind: 'error', text: 'Could not queue the edit.', detail: res.error });
      }
    }

    // Keyboard flow: advance only when the commit didn't fail — an
    // invalid cell keeps focus so the operator can fix it in place.
    if (res.status === 'queued' || res.status === 'noop') {
      if (key) {
        const grid = { rows: filtered.length, cols: editableCols.length };
        const next = nextCell(grid, pos, key);
        setEditingCell(next.row === pos.row && next.col === pos.col ? null : next);
      } else {
        setEditingCell(null); // blur-commit closes the editor
      }
    }
  };

  /** Escape: revert the draft and stay on the origin cell (edit closed —
   *  nextCell(…, 'Escape') is the identity, so nothing moves). */
  const handleCellCancel = (account: Account, field: string) => {
    const cellKey = `${account.id}:${field}`;
    setCellStates((s) => {
      const { [cellKey]: _drop, ...rest } = s;
      return rest;
    });
    setEditingCell(null);
  };

  const handleAdd = async () => {
    const created = await createAccount({ name: 'New account' });
    setOpenAccountId(created.id);
  };

  /**
   * Bulk-create accounts from the import modal. Dedup against existing
   * account names (case-insensitive) so re-running the same paste is
   * idempotent — already-present names are skipped, not duplicated.
   */
  const handleImport = async (names: string[]) => {
    const fresh = filterNewNames(names, accounts.map((a) => a.name));
    for (const name of fresh) {
      await createAccount(buildAccountDraft(name));
    }
    const skipped = names.length - fresh.length;
    push({
      kind: fresh.length > 0 ? 'success' : 'info',
      text:
        fresh.length > 0
          ? `Created ${fresh.length} account${fresh.length === 1 ? '' : 's'}.`
          : 'No new accounts to create.',
      detail:
        skipped > 0
          ? `Skipped ${skipped} that already exist${skipped === 1 ? 's' : ''}.`
          : undefined,
    });
    setImportOpen(false);
  };

  const openAccount = openAccountId
    ? accounts.find((a) => a.id === openAccountId) ?? null
    : null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Governed CRM-hygiene superpower — inline triage card over the same
          records this view renders (worker-drafted, reversible bundle). */}
      <CrmHygieneCard compact />

      {/* Toolbar */}
      <div className="kanban-toolbar">
        <div className="kanban-search">
          <Search size={13} className="kanban-search__icon" />
          <input
            className="glass-input"
            placeholder="Search accounts by name, POC, owner, workflow…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
            style={{ paddingLeft: 32 }}
          />
        </div>
        <div className="kanban-phasefilter" role="tablist">
          {(Object.keys(PHASE_STYLES) as PipelinePhase[]).map((p) => {
            const ps = PHASE_STYLES[p];
            const active = phaseFilter === p;
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={active}
                className="kanban-phasefilter__btn"
                data-active={active}
                style={
                  {
                    ['--phase-accent' as any]: ps.accent,
                    ['--phase-accent-soft' as any]: 'rgba(255,255,255,0.18)',
                  } as React.CSSProperties
                }
                onClick={() => setPhaseFilter(active ? null : p)}
              >
                <span
                  className="kanban-phasefilter__dot"
                  style={{ background: ps.accent }}
                />
                <span>{ps.label}</span>
                <span className="kanban-phasefilter__count">{phaseCounts[p]}</span>
              </button>
            );
          })}
          {phaseFilter && (
            <button
              type="button"
              className="glass-btn"
              onClick={() => setPhaseFilter(null)}
              style={{ padding: '6px 10px' }}
            >
              Clear
            </button>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {/* Views sidebar + filter-builder toggles. */}
        <button
          type="button"
          className="glass-btn"
          aria-pressed={viewsOpen}
          title={viewsOpen ? 'Hide saved views' : 'Show saved views'}
          onClick={() => setViewsOpen((o) => !o)}
        >
          <PanelLeft size={13} /> Views
        </button>
        <button
          type="button"
          className="glass-btn"
          aria-pressed={filtersOpen}
          title="Edit the active view's filters and columns"
          onClick={() => setFiltersOpen((o) => !o)}
          style={
            countLeaves(activeView.filter) > 0
              ? { borderColor: 'rgba(255,0,255,0.45)', color: '#fff' }
              : undefined
          }
        >
          <SlidersHorizontal size={13} /> Filters
          {countLeaves(activeView.filter) > 0 && (
            <span className="glass-pill glass-pill--magenta" style={{ padding: '0 6px' }}>
              {countLeaves(activeView.filter)}
            </span>
          )}
        </button>
        {/* Table / kanban lens toggle — same active view, two lenses. */}
        <div
          role="tablist"
          aria-label="Account view"
          style={{
            display: 'inline-flex',
            gap: 2,
            padding: 2,
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.03)',
          }}
        >
          {(
            [
              { id: 'table', label: 'List', Icon: List },
              { id: 'kanban', label: 'Board', Icon: LayoutGrid },
            ] as const
          ).map(({ id, label, Icon }) => {
            const active = activeView.viewType === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                className="glass-btn"
                onClick={() => handleSwitchType(activeView, id)}
                style={{
                  padding: '5px 10px',
                  border: 'none',
                  background: active
                    ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.14))'
                    : 'transparent',
                  color: active ? '#fff' : C.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon size={13} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
        {canMutate && (
          <button className="glass-btn" onClick={() => setImportOpen(true)}>
            <Upload size={13} /> Import accounts
          </button>
        )}
        {canMutate && (
          <button className="glass-btn glass-btn--primary" onClick={handleAdd}>
            <Plus size={13} /> Add account
          </button>
        )}
      </div>

      {/* Sidebar (saved views) + the active view rendered through its lens. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: viewsOpen ? 'minmax(200px, 230px) 1fr' : '1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {viewsOpen && (
          <ViewSwitcher
            views={savedViews}
            defaultViewItem={draftDefault}
            activeId={activeView.id}
            userId={userId}
            canMutate={canMutate}
            onSelect={(v) => setActiveViewId(v.id)}
            onCreate={(name) => void handleCreateView(name)}
            onTogglePin={(v) => void persistView({ ...v, pinned: !v.pinned })}
            onToggleVisibility={(v) =>
              void persistView({
                ...v,
                visibility: v.visibility === 'team' ? 'personal' : 'team',
              })
            }
            onSwitchType={handleSwitchType}
            onDelete={(v) => void handleDeleteView(v)}
          />
        )}

        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          {/* Filter-error banner: a broken view filter must be SEEN, not
              silently swallowed — the list falls back to the unfiltered
              collection while the banner explains and offers a reset. */}
          {viewStatus === 'error' && (
            <GlassPanel
              variant="subtle"
              animate={false}
              style={{
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid rgba(255,80,80,0.45)',
              }}
            >
              <AlertCircle size={15} color="#ff7b7b" style={{ flexShrink: 0 }} />
              <div role="alert" style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, color: '#fff' }}>
                  This view&apos;s filters failed to run — showing all accounts instead.
                </span>
                <span className="t-meta">{runtime.error}</span>
              </div>
              <div style={{ flex: 1 }} />
              <button type="button" className="glass-btn" onClick={clearAllFilters}>
                Clear filters
              </button>
            </GlassPanel>
          )}

          {filtersOpen && (
            <GlassPanel variant="subtle" animate={false} style={{ padding: 12, display: 'grid', gap: 12 }}>
              <FilterBuilder
                filter={activeView.filter}
                fields={FILTER_FIELD_OPTIONS}
                disabled={!canMutate && !isDefaultActive}
                onChange={(filter) => updateActiveView({ ...activeView, filter })}
              />
              {/* Column config: toggle visibility, nudge order. */}
              <div style={{ display: 'grid', gap: 6 }}>
                <span className="t-section-title">Columns</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {activeView.columns
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((col, i, arr) => (
                      <span
                        key={col.field}
                        className={col.visible ? 'glass-pill glass-pill--magenta' : 'glass-pill'}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          opacity: col.visible ? 1 : 0.55,
                        }}
                      >
                        <button
                          type="button"
                          aria-pressed={col.visible}
                          title={col.visible ? 'Hide column' : 'Show column'}
                          onClick={() =>
                            updateActiveView({
                              ...activeView,
                              columns: toggleColumn(activeView.columns, col.field),
                            })
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit',
                          }}
                        >
                          {FIELD_LABELS[col.field] ?? col.field}
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${FIELD_LABELS[col.field] ?? col.field} left`}
                          disabled={i === 0}
                          onClick={() =>
                            updateActiveView({
                              ...activeView,
                              columns: reorderColumns(activeView.columns, i, i - 1),
                            })
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: i === 0 ? 'default' : 'pointer',
                            opacity: i === 0 ? 0.35 : 1,
                            padding: 0,
                            display: 'inline-flex',
                          }}
                        >
                          <MoveLeft size={10} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${FIELD_LABELS[col.field] ?? col.field} right`}
                          disabled={i === arr.length - 1}
                          onClick={() =>
                            updateActiveView({
                              ...activeView,
                              columns: reorderColumns(activeView.columns, i, i + 1),
                            })
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: i === arr.length - 1 ? 'default' : 'pointer',
                            opacity: i === arr.length - 1 ? 0.35 : 1,
                            padding: 0,
                            display: 'inline-flex',
                          }}
                        >
                          <MoveRight size={10} />
                        </button>
                      </span>
                    ))}
                </div>
                {isDefaultActive && canMutate && (
                  <span className="t-meta">
                    Editing the default lens — press + in Views to save it as a named view.
                  </span>
                )}
              </div>
            </GlassPanel>
          )}

          {activeView.viewType === 'table' ? (
            <GlassPanel
              variant="subtle"
              animate={false}
              style={{ padding: 0, overflow: 'hidden' }}
            >
              <div
                className="crm-table"
                style={{
                  gridTemplateColumns: visibleColumns(activeView.columns)
                    .map((c) => FIELD_WIDTHS[c.field] ?? 'minmax(100px, 1fr)')
                    .join(' '),
                }}
              >
                <div className="crm-table__head">
                  {visibleColumns(activeView.columns).map((c) => (
                    <span key={c.field}>{FIELD_LABELS[c.field] ?? c.field}</span>
                  ))}
                </div>

                {viewStatus === 'loading' ? (
                  <LoadingState rows={5} label="Loading accounts" />
                ) : viewStatus === 'empty' ? (
                  // The COLLECTION is empty — teach the next step.
                  <div className="crm-empty">
                    <Inbox size={20} strokeWidth={1.75} />
                    <span>
                      {canMutate
                        ? 'No accounts yet — reset to seed or add one above.'
                        : 'No accounts yet.'}
                    </span>
                  </div>
                ) : filtersHidEverything ? (
                  // Accounts exist but the active filters hid every row —
                  // offer the one-click way out instead of a dead end.
                  <div className="crm-empty">
                    <Search size={20} strokeWidth={1.75} />
                    <span>No accounts match your filters.</span>
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={clearAllFilters}
                      style={{ marginTop: 4 }}
                    >
                      <X size={12} /> Clear filters
                    </button>
                  </div>
                ) : (
                  filtered.map((a, rowIdx) => (
                    <div
                      key={a.id}
                      role="button"
                      tabIndex={0}
                      className="crm-row"
                      onClick={() => setOpenAccountId(a.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target === e.currentTarget) {
                          setOpenAccountId(a.id);
                        }
                      }}
                    >
                      {visibleColumns(activeView.columns).map((c) => {
                        const editIdx = editableCols.indexOf(c.field);
                        if (editIdx < 0 || !canMutate) {
                          return <AccountCell key={c.field} field={c.field} account={a} />;
                        }
                        const pos: CellPos = { row: rowIdx, col: editIdx };
                        return (
                          <EditableAccountCell
                            key={c.field}
                            account={a}
                            field={c.field}
                            editing={
                              editingCell?.row === pos.row && editingCell?.col === pos.col
                            }
                            state={cellStates[`${a.id}:${c.field}`]}
                            onStart={() => setEditingCell(pos)}
                            onCommit={(value, key) =>
                              void handleCellCommit(a, c.field, value, pos, key)
                            }
                            onCancel={() => handleCellCancel(a, c.field)}
                          />
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </GlassPanel>
          ) : (
            <AccountsBoard
              byPhase={byPhase}
              empty={filtered.length === 0}
              onOpen={(id) => setOpenAccountId(id)}
            />
          )}
        </div>
      </div>

      {openAccount && (
        <AccountDetailModal
          account={openAccount}
          onClose={() => setOpenAccountId(null)}
        />
      )}

      {importOpen && (
        <ImportAccountsModal
          existingNames={accounts.map((a) => a.name)}
          onCreate={handleImport}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}

// --- Import modal ------------------------------------------------------------

/**
 * "Import accounts" — paste company names (one per line, or comma
 * separated), preview the parsed list, and bulk-create. Names that
 * already exist as accounts (case-insensitive) are flagged and skipped
 * so re-running an import is safe. The "Load ArgoIQ customers" button
 * pre-fills ARGO's own real customer names (dogfood seed — names only).
 * All parsing/dedup logic lives in the tested pure helpers in
 * `src/lib/crm/accountImport.ts`; this component just renders state.
 */
function ImportAccountsModal({
  existingNames,
  onCreate,
  onClose,
}: {
  existingNames: string[];
  onCreate: (names: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const parsed = useMemo(() => parseAccountNames(text), [text]);
  const fresh = useMemo(
    () => filterNewNames(parsed, existingNames),
    [parsed, existingNames],
  );
  const freshSet = useMemo(
    () => new Set(fresh.map((n) => n.toLowerCase())),
    [fresh],
  );

  // ESC dismisses (matches ConfirmDialog / detail-modal behaviour).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCreate = async () => {
    if (fresh.length === 0 || busy) return;
    setBusy(true);
    try {
      await onCreate(parsed);
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-accounts-title"
        tabIndex={-1}
        style={{ width: 'min(520px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background:
                  'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
                border: '1px solid rgba(255,255,255,0.16)',
                color: 'rgba(232,121,217,0.9)',
                marginRight: 10,
              }}
              aria-hidden="true"
            >
              <Upload size={16} strokeWidth={2} />
            </div>
            <h2
              id="import-accounts-title"
              className="t-section-title"
              style={{ margin: 0, flex: 1 }}
            >
              Import accounts
            </h2>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={13} />
            </button>
          </header>

          <div style={{ display: 'grid', gap: 10, padding: '8px 0 2px' }}>
            <span className="t-meta" style={{ color: C.textMuted }}>
              Paste company names — one per line (commas work too). Names that
              already exist are skipped.
            </span>
            <textarea
              className="glass-input"
              rows={7}
              placeholder={'Acme Robotics\nGlobex Corp\n…'}
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              style={{ resize: 'vertical', minHeight: 120 }}
              aria-label="Company names to import"
            />
            <div>
              <button
                type="button"
                className="glass-btn"
                onClick={() => setText(ARGOIQ_CUSTOMERS.join('\n'))}
              >
                <Building2 size={13} /> Load ArgoIQ customers
              </button>
            </div>

            {parsed.length > 0 && (
              <div style={{ display: 'grid', gap: 6 }}>
                <span className="t-meta" style={{ color: C.textMuted }}>
                  Preview — {fresh.length} new
                  {parsed.length - fresh.length > 0
                    ? `, ${parsed.length - fresh.length} already exist`
                    : ''}
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {parsed.map((name) => {
                    const isNew = freshSet.has(name.toLowerCase());
                    return (
                      <span
                        key={name.toLowerCase()}
                        className={
                          isNew ? 'glass-pill glass-pill--green' : 'glass-pill'
                        }
                        style={isNew ? undefined : { opacity: 0.55 }}
                        title={isNew ? undefined : 'Already exists — will be skipped'}
                      >
                        {name}
                        {!isNew && ' · exists'}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              paddingTop: 10,
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button type="button" className="glass-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={handleCreate}
              disabled={fresh.length === 0 || busy}
            >
              {busy
                ? 'Creating…'
                : `Create ${fresh.length} account${fresh.length === 1 ? '' : 's'}`}
            </button>
          </footer>
        </GlassPanel>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// --- Board -----------------------------------------------------------------

/**
 * AccountsBoard — a read-only phase-grouped kanban of accounts. Each
 * column is a pipeline phase (Sales / Implementations / Customer
 * Success), plus a trailing "Unassigned" column for accounts without
 * workflows. Cards open the same AccountDetailModal as the list rows.
 * Dragging stages is intentionally NOT here — that lives on the
 * per-workflow Workflows board, since an account rolls up from many.
 */
function AccountsBoard({
  byPhase,
  empty,
  onOpen,
}: {
  byPhase: Record<PipelinePhase | 'none', Account[]>;
  empty: boolean;
  onOpen: (id: string) => void;
}) {
  const columns: {
    key: PipelinePhase | 'none';
    label: string;
    accent: string;
    items: Account[];
  }[] = [
    ...BOARD_PHASES.map((p) => ({
      key: p,
      label: PHASE_STYLES[p].label,
      accent: PHASE_STYLES[p].accent,
      items: byPhase[p],
    })),
    {
      key: 'none' as const,
      label: 'Unassigned',
      accent: C.textDim,
      items: byPhase.none,
    },
  ];

  return (
    <GlassPanel variant="subtle" animate={false} className="kanban-window">
      <div className="kanban-shell">
        <div className="kanban-scroll">
          <div className="kanban-track">
            {columns.map((col) => (
              <div
                key={col.key}
                className="kanban-col"
                style={
                  {
                    ['--col-accent' as any]: col.accent,
                    ['--col-accent-soft' as any]: 'rgba(255,255,255,0.18)',
                  } as React.CSSProperties
                }
              >
                <div className="kanban-col__head">
                  <span
                    className="kanban-col__dot"
                    style={{ background: col.accent }}
                    aria-hidden="true"
                  />
                  <span className="kanban-col__title" title={col.label}>
                    {col.label}
                  </span>
                  <span className="kanban-col__count">{col.items.length}</span>
                </div>
                <div className="kanban-col__body">
                  {col.items.map((a) => (
                    <AccountKanbanCard
                      key={a.id}
                      account={a}
                      onClick={() => onOpen(a.id)}
                    />
                  ))}
                  {col.items.length === 0 && (
                    <div className="kanban-col__placeholder">
                      {empty ? 'No accounts match your filters.' : 'No accounts here.'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}

function AccountKanbanCard({
  account,
  onClick,
}: {
  account: Account;
  onClick: () => void;
}) {
  const stage = accountRollupStage(account);
  const phase = stage ? STAGE_PHASE[stage] : null;
  const phaseStyle = phase ? PHASE_STYLES[phase] : null;
  return (
    <button type="button" className="kanban-card" onClick={onClick}>
      <div className="kanban-card__name" title={account.name}>
        <span className="kanban-card__client">{account.name}</span>
      </div>
      <div className="kanban-card__poc">
        {account.pocName ? (
          `POC: ${account.pocName}`
        ) : (
          <span className="kanban-card__placeholder-text">No POC yet</span>
        )}
      </div>
      <div className="kanban-card__meta">
        {stage && phaseStyle ? (
          <span className={phaseStyle.pillClass} title={`${phaseStyle.label} · ${stage}`}>
            <span className="crm-row__dot" style={{ background: phaseStyle.accent }} />
            {stage}
          </span>
        ) : (
          <span className="t-meta">no workflows</span>
        )}
        {account.ownerName && (
          <span className="kanban-card__wf-count">{account.ownerName}</span>
        )}
      </div>
    </button>
  );
}

/**
 * One table cell for a configurable column. Fields map to the same
 * renderings the fixed table used; unknown fields fall back to a plain
 * string read so custom columns degrade honestly instead of crashing.
 */
function AccountCell({ field, account }: { field: string; account: Account }) {
  switch (field) {
    case 'name':
      return (
        <span className="crm-row__name">
          <span className="crm-row__avatar">
            <Building2 size={14} strokeWidth={2} />
          </span>
          <span>{account.name}</span>
        </span>
      );
    case 'pocName':
      return (
        <span className="crm-row__poc">
          {account.pocName ? (
            <>
              <UserCircle2 size={12} /> {account.pocName}
            </>
          ) : (
            <span className="crm-row__dim">—</span>
          )}
        </span>
      );
    case 'ownerName':
      return (
        <span>
          {account.ownerName || <span className="crm-row__dim">unassigned</span>}
        </span>
      );
    case 'stage': {
      const stage = accountRollupStage(account);
      const phase = stage ? STAGE_PHASE[stage] : null;
      const phaseStyle = phase ? PHASE_STYLES[phase] : null;
      return (
        <span>
          {stage && phaseStyle ? (
            <span className={phaseStyle.pillClass} title={`${phaseStyle.label} · ${stage}`}>
              <span className="crm-row__dot" style={{ background: phaseStyle.accent }} />
              {stage}
            </span>
          ) : (
            <span className="crm-row__dim">—</span>
          )}
        </span>
      );
    }
    case 'workflowCount':
      return (
        <span>
          {account.workflows.length === 0 ? (
            <span className="crm-row__dim">none</span>
          ) : (
            <span className="crm-row__wfcount">{account.workflows.length}</span>
          )}
        </span>
      );
    case 'updatedAt':
      return <span className="t-meta">{fmtRelative(account.updatedAt)}</span>;
    default: {
      const raw = (account as unknown as Record<string, unknown>)[field];
      return (
        <span>
          {raw === undefined || raw === null || raw === '' ? (
            <span className="crm-row__dim">—</span>
          ) : (
            String(raw)
          )}
        </span>
      );
    }
  }
}

/**
 * One GOVERNED editable table cell. Click to edit in place; the commit
 * routes through commitCellEdit → queueApproval (never a direct store
 * write), and the cell renders its own lifecycle: spinner while the
 * diff is queuing, a "queued" clock pill once it's awaiting approval,
 * and a red border + tooltip when validation or the enqueue fails.
 * Escape reverts the draft; Enter/Tab/Shift-Tab commit and move.
 */
function EditableAccountCell({
  account,
  field,
  editing,
  state,
  onStart,
  onCommit,
  onCancel,
}: {
  account: Account;
  field: string;
  editing: boolean;
  state?: CellState;
  onStart: () => void;
  onCommit: (value: string, key: 'Enter' | 'Tab' | 'ShiftTab' | null) => void;
  onCancel: () => void;
}) {
  const raw = (account as unknown as Record<string, unknown>)[field];
  const current = raw === undefined || raw === null ? '' : String(raw);
  const [draft, setDraft] = useState(current);
  // Guards the blur-commit after an explicit key commit/cancel already ran.
  const settled = useRef(false);

  // (Re)seed the draft each time this cell enters edit mode.
  useEffect(() => {
    if (editing) {
      setDraft(current);
      settled.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const isError = state?.status === 'error';
  const isPending = state?.status === 'pending';

  if (editing) {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <input
          className="glass-input"
          autoFocus
          value={draft}
          disabled={isPending}
          aria-invalid={isError || undefined}
          aria-label={`Edit ${FIELD_LABELS[field] ?? field}`}
          title={isError ? state?.error : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              settled.current = true;
              onCommit(draft, 'Enter');
            } else if (e.key === 'Tab') {
              e.preventDefault();
              settled.current = true;
              onCommit(draft, e.shiftKey ? 'ShiftTab' : 'Tab');
            } else if (e.key === 'Escape') {
              // Revert: the draft is discarded, the origin cell keeps focus.
              e.stopPropagation();
              settled.current = true;
              setDraft(current);
              onCancel();
            }
          }}
          onBlur={() => {
            if (!settled.current) onCommit(draft, null);
          }}
          style={{
            width: '100%',
            padding: '4px 8px',
            fontSize: 13,
            border: isError
              ? '1px solid rgba(255, 80, 80, 0.85)'
              : '1px solid rgba(255, 0, 255, 0.35)',
            borderRadius: 8,
          }}
        />
      </span>
    );
  }

  const display = current.trim() !== '' ? current : null;
  return (
    <span
      className={field === 'pocName' ? 'crm-row__poc' : undefined}
      title={
        isError ? state?.error : `Click to edit ${FIELD_LABELS[field] ?? field}`
      }
      onClick={(e) => {
        e.stopPropagation();
        onStart();
      }}
      style={
        isError
          ? { boxShadow: 'inset 0 0 0 1px rgba(255, 80, 80, 0.85)' }
          : undefined
      }
    >
      {field === 'pocName' && display && <UserCircle2 size={12} />}
      {display ?? (
        <span className="crm-row__dim">
          {field === 'ownerName' ? 'unassigned' : '—'}
        </span>
      )}
      {isPending && (
        <Loader2
          size={12}
          aria-label="Queuing edit"
          style={{ animation: 'auth-spin 0.8s linear infinite', color: C.textMuted }}
        />
      )}
      {state?.status === 'queued' && (
        <span
          className="glass-pill"
          title="Queued — awaiting approval in the inbox"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 6px' }}
        >
          <Clock3 size={10} /> queued
        </span>
      )}
      {isError && (
        <AlertCircle size={12} aria-label={state?.error} style={{ color: '#ff6b6b' }} />
      )}
    </span>
  );
}

function fmtRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
